import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  isMonetizationPage,
  isTermsOrPolicyPage,
  isUserVideoPage,
} from 'helpers/route';
import { getAuthenticated } from 'store/auth/selectors';
import { getActiveSystemPopupSelector } from 'store/systemPopup/selectors';

import { POPUP_QUEUE_DELAY, REGISTER_POPUPS } from './contstants';
import { PopupData, PopupType } from './types';
import {
  addPopupToStorageQueue,
  createPopupEvent,
  getPopupQueueFromStorage,
  setPopupQueueToStorage,
} from './utils';

interface LocationState {
  popup: {
    name: string;
    data?: any;
  };
}
interface PopupState {
  name: string;
  data?: any;
}

export interface OpenPopupPayload {
  name: string;
  data: PopupData<PopupType>;
}

export interface UpdatePopupPayload {
  name: string;
  data: Partial<PopupData<PopupType>>;
}

interface IPopupContext {
  handleOpenPopupForce: (popup: OpenPopupPayload) => void;
  handleOpenPopupASAP: (popup: OpenPopupPayload) => void;
  handleUpdatePopupData: (action: UpdatePopupPayload) => void;
  handleClosePopup: () => void;
  isAnyPopupOpen: () => boolean;
  checkIsPopupOpen: (name: string) => boolean;
  checkIsPopupInQueue: (name: string) => boolean;
  removePopupFromQueue: (name: string) => void;
}

export const PopupContext = createContext<IPopupContext>({
  handleOpenPopupForce: () => {},
  handleOpenPopupASAP: () => {},
  handleUpdatePopupData: () => {},
  handleClosePopup: () => {},
  isAnyPopupOpen: () => false,
  checkIsPopupOpen: () => false,
  checkIsPopupInQueue: () => false,
  removePopupFromQueue: () => {},
});

export const PopupProvider: React.FC = ({ children }) => {
  const location = useLocation<LocationState | null>();
  const currentLocation = useRef<{ pathname: string; search: string }>(
    location
  );
  const [activePopup, setActivePopup] = useState<PopupState | null>(null);

  const isAuthenticated = useSelector(getAuthenticated);
  const activeSystemPopup = useSelector(getActiveSystemPopupSelector);

  const handleOpenPopupForce = useCallback((popup: OpenPopupPayload) => {
    setActivePopup(popup);
  }, []);

  const handleOpenPopupASAP = (popup: OpenPopupPayload) => {
    const popupQueue = getPopupQueueFromStorage();

    if (
      !activePopup &&
      !popupQueue.length &&
      !isMonetizationPage(currentLocation.current.pathname) &&
      !isTermsOrPolicyPage(currentLocation.current.pathname) &&
      !isUserVideoPage(currentLocation.current.pathname)
    ) {
      handleOpenPopupForce(popup);
    } else {
      addPopupToStorageQueue(popup);
    }
  };

  const handleUpdatePopupData = (popup: UpdatePopupPayload) => {
    if (popup.name === activePopup?.name) {
      setActivePopup((prevState) => {
        return {
          ...prevState,
          data: {
            ...prevState?.data,
            ...popup.data,
          },
        };
      });
    }
  };

  const handleClosePopup = useCallback(() => {
    setActivePopup(null);
  }, []);

  const getActivePopup = () => {
    if (activePopup && isAuthenticated) {
      const PopupComponent = REGISTER_POPUPS.find(
        (popup) => popup.name === activePopup.name
      )?.component;

      if (PopupComponent) {
        return (
          <PopupComponent
            data={activePopup.data}
            onAction={(action: unknown) =>
              createPopupEvent({
                popup: activePopup.name,
                action,
              })
            }
          />
        );
      }
      handleClosePopup();
    }
    return null;
  };

  const removePopupFromQueue = (name: string) => {
    const updatedQueue = getPopupQueueFromStorage().filter(
      (popup) => popup.name !== name
    );

    setPopupQueueToStorage(updatedQueue);
  };

  const isAnyPopupOpen = useCallback(() => {
    return Boolean(activePopup);
  }, [activePopup]);

  const checkIsPopupOpen = useCallback(
    (name: string) => {
      return activePopup?.name === name;
    },
    [activePopup?.name]
  );

  const checkIsPopupInQueue = (name: string) => {
    const queue = getPopupQueueFromStorage();

    return !!queue.find((popup) => popup.name === name);
  };

  useEffect(() => {
    currentLocation.current = location;
  }, [location, location.state]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;

    if (
      isAuthenticated &&
      !activePopup &&
      !activeSystemPopup &&
      !isMonetizationPage(location.pathname) &&
      !isTermsOrPolicyPage(location.pathname) &&
      !isUserVideoPage(location.pathname)
    ) {
      const queue = getPopupQueueFromStorage();

      if (queue.length) {
        timerId = setTimeout(() => {
          const popup = queue[0];
          setPopupQueueToStorage(getPopupQueueFromStorage().slice(1));

          handleOpenPopupForce(popup);
        }, POPUP_QUEUE_DELAY);
      }
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    };
  }, [
    activePopup,
    activeSystemPopup,
    handleOpenPopupForce,
    isAuthenticated,
    location.pathname,
  ]);

  return (
    <PopupContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        handleOpenPopupForce,
        handleOpenPopupASAP,
        handleUpdatePopupData,
        handleClosePopup,
        isAnyPopupOpen,
        checkIsPopupOpen,
        checkIsPopupInQueue,
        removePopupFromQueue,
      }}
    >
      {children}
      {getActivePopup()}
    </PopupContext.Provider>
  );
};
